import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, Clock, BookOpen, Filter, Rss } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useLeadDialog } from '../context/LeadDialogContext';

const TAGS = ['All', 'AI Agents', 'Observability', 'Platform', 'Engineering', 'Customer Stories'];

const POSTS = [
  {
    id: 1,
    tag: 'AI Agents',
    title: 'Designing agents that act, not just answer',
    excerpt: 'Why action-grounded agents — armed with policies, telemetry and rollback — outperform chat-only copilots in production operations.',
    author: 'Aarav Mehta',
    role: 'Principal Engineer',
    readMin: 8,
    date: 'Feb 12, 2026',
    cover: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&q=70',
  },
  {
    id: 2,
    tag: 'Observability',
    title: 'From signals to context: rebuilding the operations graph',
    excerpt: 'How MetaxuOne stitches metrics, traces, logs and topology into a single context model SRE teams can reason about.',
    author: 'Sara Iyer',
    role: 'Product Lead, Observability',
    readMin: 11,
    date: 'Feb 5, 2026',
    cover: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=1200&q=70',
  },
  {
    id: 3,
    tag: 'Platform',
    title: 'A field guide to safe autonomous remediation',
    excerpt: 'Guardrails, audit trails and human-in-the-loop patterns that make self-healing trustworthy in regulated environments.',
    author: 'Devansh Kapoor',
    role: 'Platform Architect',
    readMin: 9,
    date: 'Jan 28, 2026',
    cover: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=1200&q=70',
  },
  {
    id: 4,
    tag: 'Engineering',
    title: 'Inside our event pipeline: 12M events/sec, single-digit ms p99',
    excerpt: 'The architecture behind MetaxuOne&apos;s ingestion plane — partitioning, backpressure, and how we cut tail latency.',
    author: 'Nikhil Rao',
    role: 'Staff Engineer',
    readMin: 14,
    date: 'Jan 18, 2026',
    cover: 'https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=1200&q=70',
  },
  {
    id: 5,
    tag: 'Customer Stories',
    title: 'How a global bank cut MTTR by 63% in six weeks',
    excerpt: 'A walkthrough of the rollout, the wins, and the lessons learned moving from alert fatigue to agent-led triage.',
    author: 'Priya Natarajan',
    role: 'Customer Success',
    readMin: 6,
    date: 'Jan 10, 2026',
    cover: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&q=70',
  },
  {
    id: 6,
    tag: 'AI Agents',
    title: 'Agent memory: short, long, and operational',
    excerpt: 'A practical taxonomy of memory for agents that operate on living systems — and how to keep it bounded.',
    author: 'Aarav Mehta',
    role: 'Principal Engineer',
    readMin: 7,
    date: 'Dec 22, 2025',
    cover: 'https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=1200&q=70',
  },
  {
    id: 7,
    tag: 'Observability',
    title: 'Why dashboards are not enough anymore',
    excerpt: 'The shift from passive visualization to active, conversational operations driven by AI assistants.',
    author: 'Sara Iyer',
    role: 'Product Lead',
    readMin: 5,
    date: 'Dec 14, 2025',
    cover: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=1200&q=70',
  },
];

const Blog = () => {
  const { open: openLead } = useLeadDialog();
  const [tag, setTag] = useState('All');
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const filtered = POSTS.filter((p) => tag === 'All' || p.tag === tag);
  const featured = filtered[0] || POSTS[0];
  const rest = (filtered[0] ? filtered.slice(1) : filtered).slice(0, 9);

  return (
    <div className="bg-black min-h-screen" data-testid="blog-page">
      <Navbar />
      <main>
        {/* HERO */}
        <section className="relative overflow-hidden bg-black border-b border-neutral-900">
          <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(60% 50% at 80% 0%, rgba(59,130,246,0.22), transparent), radial-gradient(50% 60% at 10% 30%, rgba(255,255,255,0.05), transparent)',
          }} />
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-16">
            <nav className="flex items-center gap-1.5 text-[12.5px] text-neutral-500">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-neutral-400">Resources</span>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-neutral-300">Blog</span>
            </nav>
            <div className="mt-7 flex flex-wrap items-end justify-between gap-6">
              <div className="max-w-3xl">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30">
                  <BookOpen className="w-3.5 h-3.5 text-blue-400" />
                  <span className="text-[11.5px] uppercase tracking-[0.15em] text-blue-300">MetaxuOne Blog</span>
                </div>
                <h1 className="mt-4 text-[34px] sm:text-[48px] md:text-[60px] lg:text-[72px] leading-[1.02] sm:leading-[0.98] tracking-[-0.03em] font-semibold text-white">
                  Ideas on <span className="italic font-light brand-gradient-text">autonomous operations.</span>
                </h1>
                <p className="mt-5 text-[16.5px] lg:text-[18px] text-neutral-300 leading-relaxed">
                  Field notes from our engineers, product leads and customers — on AI agents, observability, platform design and the work of running modern enterprises.
                </p>
              </div>
              <Button
                onClick={() => openLead({ source: 'blog-subscribe', title: 'Subscribe to MetaxuOne updates' })}
                className="h-11 px-5 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[13.5px] font-medium inline-flex items-center gap-2"
                data-testid="blog-subscribe-btn"
              >
                <Rss className="w-4 h-4" /> Subscribe
              </Button>
            </div>
          </div>
        </section>

        {/* FILTERS */}
        <section className="bg-neutral-950 border-b border-neutral-900">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex items-center gap-2 flex-wrap">
            <Filter className="w-3.5 h-3.5 text-neutral-500" />
            {TAGS.map((t) => (
              <button
                key={t}
                onClick={() => setTag(t)}
                data-testid={`blog-filter-${t.replace(/\s+/g, '-').toLowerCase()}`}
                className={`text-[12.5px] px-3 py-1.5 rounded-full border transition-colors ${
                  tag === t
                    ? 'bg-blue-500/20 border-blue-500/50 text-blue-200'
                    : 'border-neutral-800 text-neutral-300 hover:border-neutral-600'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </section>

        {/* FEATURED */}
        <section className="relative bg-black border-b border-neutral-900">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
            <p className="text-[12.5px] uppercase tracking-[0.18em] text-blue-400">Featured</p>
            <article className="mt-6 grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7 relative rounded-2xl overflow-hidden border border-neutral-800">
                <img src={featured.cover} alt={featured.title} className="w-full h-[280px] lg:h-[440px] object-cover" />
                <div className="absolute top-4 left-4 text-[11px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full bg-black/60 border border-white/10 text-white">
                  {featured.tag}
                </div>
              </div>
              <div className="lg:col-span-5">
                <h2 className="text-[26px] sm:text-[34px] lg:text-[42px] leading-[1.07] tracking-[-0.02em] font-semibold text-white">
                  {featured.title}
                </h2>
                <p className="mt-5 text-[15.5px] text-neutral-300 leading-relaxed">{featured.excerpt}</p>
                <div className="mt-6 flex items-center gap-4 text-[12.5px] text-neutral-400">
                  <span>{featured.author} · <span className="text-neutral-500">{featured.role}</span></span>
                  <span>•</span>
                  <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {featured.readMin} min read</span>
                  <span>•</span>
                  <span>{featured.date}</span>
                </div>
                <Button
                  onClick={() => openLead({ source: `blog-${featured.id}`, title: featured.title })}
                  className="mt-7 h-12 px-6 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center gap-2"
                  data-testid="blog-featured-read-btn"
                >
                  Read article <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </article>
          </div>
        </section>

        {/* GRID */}
        <section className="bg-neutral-950 border-b border-neutral-900">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
            <p className="text-[12.5px] uppercase tracking-[0.18em] text-blue-400">Latest posts</p>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {rest.map((p) => (
                <article key={p.id} className="group rounded-2xl border border-neutral-800 bg-black overflow-hidden hover:border-neutral-600 transition-colors flex flex-col">
                  <div className="relative h-44 overflow-hidden">
                    <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                    <div className="absolute top-3 left-3 text-[10.5px] uppercase tracking-[0.16em] px-2 py-1 rounded-full bg-black/60 border border-white/10 text-white">
                      {p.tag}
                    </div>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="text-[16.5px] font-semibold text-white leading-snug">{p.title}</h3>
                    <p className="mt-2 text-[13.5px] text-neutral-400 leading-snug line-clamp-2">{p.excerpt}</p>
                    <div className="mt-auto pt-4 flex items-center justify-between text-[12px] text-neutral-500">
                      <span>{p.author}</span>
                      <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {p.readMin} min</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-black">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24 text-center">
            <h3 className="text-[28px] sm:text-[34px] lg:text-[42px] leading-[1.05] tracking-[-0.02em] font-semibold text-white">
              Get the next post <span className="italic font-light brand-gradient-text">in your inbox.</span>
            </h3>
            <p className="mt-4 text-[15.5px] text-neutral-400 max-w-2xl mx-auto">
              One concise note every other week. Engineering deep-dives, operational playbooks, and the occasional opinion.
            </p>
            <div className="mt-7 inline-flex">
              <Button
                onClick={() => openLead({ source: 'blog-bottom-subscribe', title: 'Subscribe to MetaxuOne updates' })}
                className="h-12 px-6 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center gap-2"
                data-testid="blog-bottom-subscribe-btn"
              >
                Subscribe <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Blog;
